// src/components/StudentTestimonialsSection.jsx

import { useEffect, useState } from "react";
import { motion } from "framer-motion";

import TestimonialCard from "./TestimonialCard";
import testimonials from "../data/testimonials";

const StudentTestimonialsSection = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    if (!testimonials.length) {
      return undefined;
    }

    const interval = setInterval(() => {
      setCurrentTestimonial(
        (prev) => (prev + 1) % testimonials.length
      );
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  if (!testimonials.length) {
    return null;
  }

  return (
    <section
      aria-labelledby="student-testimonials-heading"
      className="
        relative
        overflow-hidden
        px-4
        py-12
        sm:px-6
        md:py-20
      "
    >
      {/* Background Accents */}
      <div
        className="
          pointer-events-none
          absolute
          inset-0
        "
        aria-hidden="true"
      >
        <div
          className="
            absolute
            left-1/2
            top-1/4
            h-[300px]
            w-[300px]
            -translate-x-1/2
            rounded-full
            bg-purple-200/30
            blur-3xl
            dark:bg-purple-700/30
            sm:h-[400px]
            sm:w-[400px]
          "
        />

        <div
          className="
            absolute
            bottom-0
            right-1/3
            h-[400px]
            w-[400px]
            rounded-full
            bg-pink-200/30
            blur-3xl
            dark:bg-pink-700/30
            sm:h-[500px]
            sm:w-[500px]
          "
        />
      </div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="
          relative
          z-10
          mb-12
          px-4
          text-center
          md:px-0
        "
      >
        <h2
          id="student-testimonials-heading"
          className="
            relative
            inline-block
            bg-gradient-to-r
            from-pink-500
            via-purple-500
            to-indigo-500
            bg-clip-text
            text-2xl
            font-bold
            text-transparent
            sm:text-3xl
            md:text-4xl
          "
        >
          What Our Students Say

          <span
            aria-hidden="true"
            className="
              absolute
              -bottom-2
              left-1/2
              h-1
              w-32
              -translate-x-1/2
              rounded-full
              bg-gradient-to-r
              from-pink-400
              via-purple-400
              to-indigo-400
              opacity-90
            "
          />
        </h2>

        <p
          className="
            mx-auto
            mt-4
            max-w-2xl
            text-base
            leading-relaxed
            text-gray-700
            dark:text-gray-300
            sm:text-lg
            md:text-xl
          "
        >
          Read experiences shared by our{" "}
          <span className="font-semibold text-pink-500">
            students
          </span>{" "}
          and{" "}
          <span className="font-semibold text-purple-500">
            professionals
          </span>{" "}
          who have trained with{" "}
          <span className="font-bold text-indigo-500 dark:text-indigo-400">
            IA4Solutions
          </span>
          .
        </p>
      </motion.div>

      {/* Testimonial Carousel */}
      <div
        className="
          relative
          z-10
          mx-auto
          flex
          min-h-[400px]
          max-w-5xl
          items-center
          justify-center
          px-2
          sm:min-h-[420px]
          sm:px-4
          md:min-h-[380px]
        "
      >
        {testimonials.map((testimonial, index) => (
          <motion.div
            key={testimonial.id || index}
            className={`
              absolute
              inset-0
              flex
              items-center
              justify-center
              transition-all
              duration-1000
              ${
                index === currentTestimonial
                  ? "z-10 opacity-100"
                  : "z-0 opacity-0"
              }
            `}
          >
            <TestimonialCard
              {...testimonial}
              layout="horizontal"
            />
          </motion.div>
        ))}
      </div>

      {/* Bottom Gradient */}
      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          bottom-0
          left-0
          right-0
          h-16
          bg-gradient-to-t
          from-gray-50
          to-transparent
          dark:from-gray-900
        "
      />
    </section>
  );
};

export default StudentTestimonialsSection;